import { Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { fuseAnimations } from 'app/projects/fuse/src/lib/animations';

import { FlatTreeControl } from '@angular/cdk/tree';
import { FormControl } from '@angular/forms';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { Navigate } from '@ngxs/router-plugin';
import { Store } from '@ngxs/store';
import { CourseCatalogPostingStream } from 'app/library/course-catalog-landing-page/models/course-catalog-posting-stream.model';
import { CourseCatalogLandingPageApiService } from 'app/library/course-catalog-landing-page/services/course-catalog-landing-page.api-service';
import { AudienceRelevantExperience } from 'app/library/grow/models/audience-relevant-experience.model';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { EngageCategory } from '../../../posting/models/engage-category.model';
import { PostingType } from '../../../posting/models/posting-type.model';
import { PostingTypeApiService } from '../../../posting/services/posting-type.api-service';
import { PostingService } from '../../../posting/services/posting.service';
import { ILiveCategoryLinkActionPayload } from '../../interfaces/live-category-link.action-payload.interface';
import { LiveCategoryLink } from '../../models/live-category-link.model';
import { LiveCategoryLinkApiService } from '../../services/live-category-link.api-service';

interface ICourseStructureCategoryFlatNode {
    expandable: boolean;
    id: string;
    name: string;
    type: string;
    isSelected: boolean;
    numberOfResults: number;
    level: number;
    offerringGroupOfferings?: CourseCatalogPostingStream[];
    isLoading: boolean;
    isFetched: boolean;
    isParentSelected?: boolean;
}

@Component({
    selector: 'app-live-category-link-page',
    templateUrl: './live-category-link-page.component.html',
    styleUrls: ['./live-category-link-page.component.scss'],
    encapsulation: ViewEncapsulation.None,
    animations: fuseAnimations,
})
export class LiveCategoryLinkPageComponent extends UnsubscribeOnDestroy implements OnInit {
    @Input()
    audienceRelevantExperiences: AudienceRelevantExperience[] = [];

    @Input()
    audienceId: string;

    @Input()
    featuredStreamContentId: string;

    @Input()
    selectedContentTypeId: string;

    @Input()
    liveCategoryLinks: LiveCategoryLink[] = [];

    @Output()
    closeDialog = new EventEmitter<boolean>();

    searchInput = new FormControl('');
    fetchCourseStructureCategoriesDataAsTree = false;
    selectedCategories: ICourseStructureCategoryFlatNode[] = [];
    isLoading: boolean;
    postingTypeInput = new FormControl();
    postingTypes: PostingType[];

    get hasSelectionChanges(): boolean {
        const selectedCategoryIds = this._getSelectedCategoryIds();
        const existingCategoryIds = new Set(this.liveCategoryLinks.map((link) => Number(link.category.id)));

        if (selectedCategoryIds.size !== existingCategoryIds.size) {
            return true;
        }

        return [...selectedCategoryIds].some((id) => !existingCategoryIds.has(id));
    }

    private _transformer = (node: EngageCategory, level: number) => {
        return {
            expandable: !!node.children && node.children.length > 0,
            id: node.category.id,
            name: node.category.name,
            type: node.category.type,
            numberOfResults: node.numberOfResults,
            isSelected: node.category.isSelected,
            level: level,
        };
    };

    treeControl = new FlatTreeControl<ICourseStructureCategoryFlatNode>(
        (node) => node.level,
        (node) => node.expandable
    );

    treeFlattener = new MatTreeFlattener(
        this._transformer,
        (node) => node.level,
        (node) => node.expandable,
        (node) => node.children
    );

    dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);

    constructor(
        private _store: Store,
        private _postingService: PostingService,
        private _courseCatalogApiService: CourseCatalogLandingPageApiService,
        private _postingTypeApiService: PostingTypeApiService,
        private _liveCategoryLinkApiService: LiveCategoryLinkApiService
    ) {
        super();
    }

    async ngOnInit(): Promise<void> {
        this.isLoading = true;

        this.postingTypeInput.valueChanges.pipe(takeUntil(this._unsubscribeAll)).subscribe(() => {
            this._loadTreeData();
        });

        this.searchInput.valueChanges.pipe(takeUntil(this._unsubscribeAll), debounceTime(500), distinctUntilChanged()).subscribe(() => {
            this._loadTreeData();
        });

        this._loadTreeData();
    }

    private async _loadTreeData(): Promise<void> {
        this.dataSource.data = await this._postingService.fetchEngageCategories(this.audienceId);
        this.selectedCategories = [];

        // Mark already selected categories
        setTimeout(() => {
            if (this.liveCategoryLinks?.length) {
                const existingIds = this.liveCategoryLinks.map((x) => String(x.category.id));

                this.treeControl.dataNodes.forEach((node) => {
                    if (existingIds.includes(node.id)) {
                        node.isSelected = true;
                        this.selectedCategories.push(node);

                        // Mark children
                        this.markChildrenAsParentSelected(node, true);
                    }
                });

                // Force UI refresh
                this.treeControl.dataNodes = [...this.treeControl.dataNodes];
            }
        });

        this.isLoading = false;
    }

    hasChild = (_: number, node: ICourseStructureCategoryFlatNode) => node.expandable;

    isNodeExpandable(node: ICourseStructureCategoryFlatNode): boolean {
        // If subcategories, expandable only if children exist
        if (node.type === 'sub_categories') {
            return node.expandable;
        }

        // If offerings, expandable only if there are results
        if (node.type === 'offerings') {
            return node.numberOfResults > 0;
        }

        return false;
    }

    expandNode(node: ICourseStructureCategoryFlatNode, event: MouseEvent): void {
        if (!this.isNodeExpandable(node)) {
            event.stopPropagation();
            return;
        }

        this.fetchCourseOfferingsByCategory(node);
    }

    private markChildrenAsParentSelected(parentNode: ICourseStructureCategoryFlatNode, value: boolean): void {
        const nodes = this.treeControl.dataNodes;
        const startIndex = nodes.findIndex((n) => n.id === parentNode.id && n.level === parentNode.level);

        if (startIndex === -1) {
            return;
        }

        for (let i = startIndex + 1; i < nodes.length; i++) {
            const node = nodes[i];

            if (node.level <= parentNode.level) {
                break;
            }

            node.isParentSelected = value;
        }

        // Force UI refresh
        this.treeControl.dataNodes = [...this.treeControl.dataNodes];
    }

    navigateToChangeExperience(): void {
        this._store.dispatch(new Navigate([`/admin/grow/audiences/${this.audienceId}/relevant-experiences`]));

        this.closeDialog.emit(true);
    }

    async fetchCourseOfferingsByCategory(categoryItem: ICourseStructureCategoryFlatNode): Promise<void> {
        if (categoryItem.type === 'offerings' && !categoryItem.isFetched && categoryItem.numberOfResults && this.treeControl.isExpanded(categoryItem)) {
            categoryItem.isLoading = true;

            categoryItem.offerringGroupOfferings = await this._courseCatalogApiService.getCategoryWithOfferingsPostings(categoryItem.id, '', 100, 1).toPromise();

            categoryItem.isFetched = true;
            categoryItem.isLoading = false;
        }
    }

    onSelectPostingStream(categoryWithPosting: ICourseStructureCategoryFlatNode): void {
        if (this.selectedCategories.some((category) => category.id === categoryWithPosting.id)) {
            return;
        }

        categoryWithPosting.isSelected = true;
        this.selectedCategories.push(categoryWithPosting);

        this.markChildrenAsParentSelected(categoryWithPosting, true);
    }

    onDeselectPostingStream(categoryWithPosting: ICourseStructureCategoryFlatNode): void {
        categoryWithPosting.isSelected = false;
        this.selectedCategories = this.selectedCategories.filter((p) => p.id !== categoryWithPosting.id);

        this.markChildrenAsParentSelected(categoryWithPosting, false);
    }

    removeFromList(categoryWithPosting: ICourseStructureCategoryFlatNode): void {
        this.onDeselectPostingStream(categoryWithPosting);
    }

    onClearSelection(): void {
        this.selectedCategories = [];

        this.treeControl.dataNodes.forEach((node) => {
            node.isSelected = false;
            node.isParentSelected = false;
        });

        // Force UI refresh
        this.treeControl.dataNodes = [...this.treeControl.dataNodes];
    }

    async save(): Promise<void> {
        this.isLoading = true;

        const selectedCategoryIds = [...this._getSelectedCategoryIds()];
        const linksToRemove = this.liveCategoryLinks.filter((link) => !selectedCategoryIds.includes(Number(link.category.id)));

        if (linksToRemove.length) {
            await Promise.all(linksToRemove.map((link) => this._liveCategoryLinkApiService.removeLiveCategoryLink(link.id).toPromise()));
        }

        if (selectedCategoryIds.length) {
            const data: ILiveCategoryLinkActionPayload = {
                audience: { id: this.audienceId },
                featuredStream: { id: this.featuredStreamContentId },
                categoryIds: selectedCategoryIds,
            };

            await this._liveCategoryLinkApiService.createLiveCategoryLinks(data).toPromise();
        }

        this.isLoading = false;

        this.closeDialog.emit(false);
    }

    private _getSelectedCategoryIds(): Set<number> {
        return new Set(this.selectedCategories.map((postingStream) => Number(postingStream.id)));
    }
}
